import { $isCodeNode, CODE_LANGUAGE_MAP } from '@lexical/code';
import {
    $isListNode,
    INSERT_CHECK_LIST_COMMAND,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListNode,
    REMOVE_LIST_COMMAND
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createHeadingNode, $createQuoteNode, $isHeadingNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $isTableNode, $isTableSelection } from '@lexical/table';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { Divider } from '@mui/material';
import { $createParagraphNode, $getSelection, $isRangeSelection, $isRootOrShadowRoot } from 'lexical';

import React, { useCallback, useEffect, useState } from 'react';
import { H } from '../Layout';
import { PUNCTUATION_MARKS_CONSTANTS } from '../constants/constants';
import AlignmentMenuItem from './AlignmentMenuItem';
import CombinedColorMenu from './CombinedColorMenu';
import ListMenuItem from './ListMenuItem';
import ParagraphMenuItem, { paragraphTypes } from './ParagraphMenuItem';
import TextStyleMenuItem from './TextStyleMenuItem';
import TextTransformMenu from './TextTransformMenu';
import UndoRedoMenuItem from './UndoRedoMenuItem';
import { getSelectedNode } from './getSelectedNode';
import { fontFamilyKey, fontSizeKey } from './lexicalConstants';
import LexicalFontMenu from './lexicalToolbarComponents/LexicalFontMenu';
import LexicalFontSizeToolbar from './lexicalToolbarComponents/LexicalFontSizeToolbar';
import LexicalInsertMenu from './lexicalToolbarComponents/LexicalInsertMenu';
import LinkAttach from './lexicalToolbarComponents/LinkAttach';

const rootTypeToRootName = {
    root: 'Root',
    table: 'Table'
};

/**
 * Renders a vertical divider element for the toolbar to visually separate groups of controls.
 */
const ToolbarDivider = () => {
    return <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />;
};

type LexicalTopToolbarProps = {
    disabled?: boolean;
    isSmall?: boolean;
};

/**
 * Main toolbar component for the Lexical editor providing text formatting, styling, and content insertion controls.
 * Manages editor state including text styles, alignment, fonts, and block types.
 */
export default (props: LexicalTopToolbarProps) => {
    const { disabled, isSmall } = props;
    const [editor] = useLexicalComposerContext();
    const [blockType, setBlockType] = useState<keyof typeof paragraphTypes>('paragraph');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);

    // might use these when implemeting code blocks
    const [rootType, setRootType] = useState<keyof typeof rootTypeToRootName>('root');
    const [isCode, setIsCode] = useState(false);
    const [codeLanguage, setCodeLanguage] = useState<string>('');

    const [isAlignLeft, setIsAlignLeft] = useState(false);
    const [isAlignCenter, setIsAlignCenter] = useState(false);
    const [isAlignRight, setIsAlignRight] = useState(false);
    const [isAlignJustify, setIsAlignJustify] = useState(false);
    const [currentFont, setCurrentFont] = useState<string>('');
    const [currentFontSize, setCurrentFontSize] = useState<string>('');

    if (!editor) {
        return null;
    }

    /**
     * Updates toolbar state based on current editor selection.
     * Syncs formatting states (bold, italic, alignment), font properties, and block type with the selected text.
     */
    const updateToolbar = useCallback(() => {
        if (editor) {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const anchorNode = selection.anchor.getNode();
                const format = anchorNode.getFormat();
                setIsAlignLeft(format === 1);
                setIsAlignCenter(format === 2);
                setIsAlignRight(format === 3);
                setIsAlignJustify(format === 4);

                let element =
                    anchorNode.getKey() === 'root'
                        ? anchorNode
                        : $findMatchingParent(anchorNode, (e) => {
                              const parent = e.getParent();
                              return parent !== null && $isRootOrShadowRoot(parent);
                          });

                if (element === null) {
                    element = anchorNode.getTopLevelElementOrThrow();
                }

                const elementKey = element.getKey();
                const elementDOM = editor.getElementByKey(elementKey);

                // Update text format
                setIsBold(selection.hasFormat('bold'));
                setIsItalic(selection.hasFormat('italic'));
                setIsUnderline(selection.hasFormat('underline'));
                setIsStrikethrough(selection.hasFormat('strikethrough'));

                setIsCode(selection.hasFormat('code'));

                // Update links and font family
                const node = getSelectedNode(selection);
                const nodeStyle = node.getStyle() || '';
                const fontFamily =
                    nodeStyle
                        ?.split(`${fontFamilyKey}: `)?.[1]
                        ?.split(`${PUNCTUATION_MARKS_CONSTANTS.semicolon}`)?.[0] || '';
                setCurrentFont(fontFamily);

                const fontSize =
                    nodeStyle.split(`${fontSizeKey}: `)?.[1]?.split(`${PUNCTUATION_MARKS_CONSTANTS.semicolon}`)?.[0] ||
                    '';

                setCurrentFontSize(fontSize);
                const parent = node.getParent();

                const tableNode = $findMatchingParent(node, $isTableNode);
                if ($isTableNode(tableNode)) {
                    setRootType('table');
                } else {
                    setRootType('root');
                }

                if (elementDOM !== null) {
                    if ($isListNode(element)) {
                        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
                        const type = parentList ? parentList.getListType() : element.getListType();
                        setBlockType(type);
                    } else {
                        const type = $isHeadingNode(element) ? element.getTag() : element.getType();
                        if (type in paragraphTypes) {
                            setBlockType(type as keyof typeof paragraphTypes);
                        }
                        if ($isCodeNode(element)) {
                            const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
                            setCodeLanguage(language ? CODE_LANGUAGE_MAP[language] || language : '');
                            return;
                        }
                    }
                }
            }
        }
    }, [editor]);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    updateToolbar();
                });
            })
        );
    }, [editor, updateToolbar]);

    /**
     * Handles changing the block type of the current selection.
     * Transforms selected text into paragraphs, headings, lists (bullet, numbered, checklist), or quotes.
     */
    const handleBlockType = (bt: keyof typeof paragraphTypes) => {
        if (blockType !== bt) {
            editor.update(() => {
                const selection = $getSelection();
                if (bt === 'paragraph') {
                    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
                        $setBlocksType(selection, () => $createParagraphNode());
                    }
                } else if (bt === 'h1' || bt === 'h2' || bt === 'h3' || bt === 'h4' || bt === 'h5' || bt === 'h6') {
                    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
                        $setBlocksType(selection, () => $createHeadingNode(bt));
                    }
                } else if (bt === 'bullet') {
                    if (blockType !== 'bullet') {
                        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
                    } else {
                        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                    }
                } else if (bt === 'number') {
                    if (blockType !== 'number') {
                        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
                    } else {
                        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                    }
                } else if (bt === 'check') {
                    if (blockType !== 'check') {
                        editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
                    } else {
                        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                    }
                } else if (bt === 'quote') {
                    if (blockType !== 'quote') {
                        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
                            $setBlocksType(selection, () => $createQuoteNode());
                        }
                    }
                }
            });
        }
    };

    return (
        <H>
            <UndoRedoMenuItem editor={editor} disabled={disabled} />
            <ToolbarDivider />
            <ParagraphMenuItem blockType={blockType} setBlockType={handleBlockType} disabled={disabled} />
            <ListMenuItem isSmall={isSmall} blockType={blockType} setBlockType={handleBlockType} disabled={disabled} />
            <ToolbarDivider />
            <LexicalFontMenu disabled={disabled} editor={editor} currentFont={currentFont} />
            <ToolbarDivider />
            <LexicalFontSizeToolbar disabled={disabled} editor={editor} currentFontSize={currentFontSize} />
            <ToolbarDivider />
            <TextStyleMenuItem
                isSmall={isSmall}
                editor={editor}
                isBold={isBold}
                isItalic={isItalic}
                isStrikethrough={isStrikethrough}
                isUnderline={isUnderline}
                disabled={disabled}
            />
            <ToolbarDivider />
            <LinkAttach editor={editor} disabled={disabled} />
            <CombinedColorMenu editor={editor} />
            <TextTransformMenu isSmall={isSmall} disabled={disabled} editor={editor} />
            <ToolbarDivider />
            <LexicalInsertMenu editor={editor} disabled={disabled} />
            <ToolbarDivider />

            <AlignmentMenuItem
                editor={editor}
                isSmall={isSmall}
                isAlignLeft={isAlignLeft}
                isAlignCenter={isAlignCenter}
                isAlignRight={isAlignRight}
                isAlignJustify={isAlignJustify}
                disabled={disabled}
            />
        </H>
    );
};
